import { useState, useCallback, useRef, useEffect, lazy, Suspense } from "react";
import { Card } from "@/components/ui/card";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useActiveLeagues, isLeagueSupportedDynamic } from "@/hooks/useActiveLeagues";
import { useStickyLeague } from "@/hooks/useUrlState";
import ManagerSearch from "@/components/ManagerSearch";
import { ManagerTableSection, Assignment } from "@/components/ManagerTableSection";
import { ManagerProfileHeader } from "@/components/ManagerProfileHeader";
import CurrentLeagueTables from "@/components/CurrentLeagueTables";
import UpcomingFixtures from "@/components/UpcomingFixtures";
import HotSeatTable from "@/components/HotSeatTable";
import LeagueSelectorCard from "@/components/LeagueSelectorCard";

// Manager-detail panels (charts, reports) load only when a manager is selected.
const ManagerCareerCard = lazy(() => import("@/components/ManagerCareerCard"));
const ManagerPressureSection = lazy(() =>
  import("@/components/ManagerPressureSection").then((m) => ({ default: m.ManagerPressureSection }))
);
const ManagerSeasonReports = lazy(() =>
  import("@/components/ManagerSeasonReports").then((m) => ({ default: m.ManagerSeasonReports }))
);
const ManagerTalkingPoints = lazy(() =>
  import("@/components/ManagerTalkingPoints").then((m) => ({ default: m.ManagerTalkingPoints }))
);
const PredictedTableCard = lazy(() => import("@/components/PredictedTableCard"));

import { websiteJsonLd, faqJsonLd, managerJsonLd } from "@/components/ManagerStructuredData";
import { Helmet } from "react-helmet-async";
import { Header } from "@/components/Header";
import { HeroBand } from "@/components/HeroBand";
import Footer from "@/components/Footer";
import { Seo } from "@/components/Seo";
import { trackEvent } from "@/lib/analytics";

interface PressureDataPoint {
  matchNumber: number;
  matchDate: string;
  hotSeatScore: number;
  pressure: number;
  result: 'W' | 'D' | 'L';
  goalsFor: number;
  goalsAgainst: number;
  isHeavyDefeat: boolean;
  position: number | null;
  expectedPosition: number | null;
  positionDelta: number;
  pressureDelta: number;
  recentForm: string;
  season: string;
  opponentPosition: number | null;
  opponentName: string | null;
  isHome: boolean;
  expectedResult: 'W' | 'D' | 'L' | null;
  resultVsExpectation: 'better' | 'expected' | 'worse' | null;
  leagueCode?: string;
}

const Index = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { data: activeLeagues = [] } = useActiveLeagues();

  // Selected league persists in the URL (back/forward) and for the session
  // (site navigation back to the home page).
  const [activeLeague, setActiveLeague] = useStickyLeague();


  const [selectedAssignment, setSelectedAssignment] = useState<Assignment | null>(null);
  const [managerName, setManagerName] = useState<string>("");
  const [pressureDataPoints, setPressureDataPoints] = useState<PressureDataPoint[]>([]);

  const selectedManagerId = searchParams.get("manager") || null;

  const { data: legacyManagerSlug } = useQuery({
    queryKey: ['manager-slug-by-id', selectedManagerId],
    queryFn: async () => {
      if (!selectedManagerId) return null;
      const { data, error } = await supabase
        .from('managers')
        .select('slug')
        .eq('id', selectedManagerId)
        .single();
      if (error) return null;
      return data?.slug || null;
    },
    enabled: !!selectedManagerId,
  });

  useEffect(() => {
    if (selectedManagerId && legacyManagerSlug) {
      navigate(`/manager/${legacyManagerSlug}`, { replace: true });
    }
  }, [selectedManagerId, legacyManagerSlug, navigate]);

  useEffect(() => {
    if (selectedManagerId) {
      window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
    }
  }, [selectedManagerId]);

  useEffect(() => {
    if (activeLeague) trackEvent('league_table_view', { league: activeLeague });
  }, [activeLeague]);


  const { data: allAssignments } = useQuery({
    queryKey: ['manager-all-assignments', selectedManagerId, activeLeagues],
    queryFn: async () => {
      if (!selectedManagerId) return [];
      const { data, error } = await supabase
        .from('manager_assignments')
        .select('id, league, start_date, end_date, club_id, clubs(name)')
        .eq('manager_id', selectedManagerId)
        .order('start_date', { ascending: true });
      if (error) throw error;
      if (!activeLeagues || activeLeagues.length === 0) return data || [];
      return (data || []).filter(a => isLeagueSupportedDynamic(a.league, activeLeagues));
    },
    enabled: !!selectedManagerId,
  });

  const handleSelectManager = useCallback((managerId: string | null, _managerName?: string, managerSlug?: string) => {
    if (managerId && managerSlug) {
      navigate(`/manager/${managerSlug}`, { replace: false });
      return;
    }
    const newParams = new URLSearchParams(searchParams);
    if (managerId) {
      newParams.set("manager", managerId);
    } else {
      newParams.delete("manager");
    }
    newParams.delete("club");
    setSearchParams(newParams, { replace: false });
    setSelectedAssignment(null);
    setManagerName("");
    setPressureDataPoints([]);
  }, [searchParams, setSearchParams, navigate]);



  const handleAssignmentChange = (assignmentId: string, assignment: Assignment | null) => {
    setSelectedAssignment(assignment);
    if (assignment) {
      setManagerName(assignment.managers.name);
    }
  };

  const handlePressureDataLoaded = (data: { dataPoints: PressureDataPoint[]; expectedPosition: number | null }) => {
    setPressureDataPoints(data.dataPoints);
  };

  return (
    <div className="min-h-screen bg-background">
      <Seo path="/" title="ManagerTable — The live pressure index for football managers" description="Live MPI pressure ratings, manager league tables by tenure and career stats across 21 domestic leagues." />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              websiteJsonLd(),
              faqJsonLd(),
              ...(managerName && selectedAssignment
                ? [
                    managerJsonLd({
                      managerName,
                      clubName: selectedAssignment.clubs.name,
                      slug: legacyManagerSlug,
                      startDate: selectedAssignment.start_date,
                      endDate: selectedAssignment.end_date,
                    }),
                  ]
                : []),
            ],
          })}
        </script>
      </Helmet>
      <Header />

      {/* Matchday hero band */}
      {!selectedManagerId && (
        <HeroBand
          leagueCount={activeLeagues.length}
          onSelectManager={(id, slug) => handleSelectManager(id, undefined, slug)}
        >
          <ManagerSearch onSelectManager={(id, name, slug) => handleSelectManager(id, name, slug)} />
        </HeroBand>
      )}

      <main className="container mx-auto px-4 py-4 md:py-8">
        <div className="max-w-5xl mx-auto space-y-4 md:space-y-6">

          {/* Collapsed search when manager is selected */}
          {selectedManagerId && !legacyManagerSlug && (
            <Card className="p-3">
              <ManagerSearch onSelectManager={(id) => handleSelectManager(id)} />
            </Card>
          )}

          {selectedManagerId && legacyManagerSlug && (
            <div className="rounded-lg border border-chrome-border bg-white/[0.04] p-8 text-center text-chrome-muted">
              <p className="text-sm">Redirecting to manager profile…</p>
            </div>
          )}

          {/* Manager detail sections */}
          {selectedManagerId && !legacyManagerSlug && (
            <div className="space-y-4 md:space-y-6">
              <ManagerProfileHeader managerId={selectedManagerId} />

              {selectedAssignment && (
                <Suspense fallback={<div className="h-64 rounded-lg border border-chrome-border bg-white/[0.03] animate-pulse" />}>
                  <ManagerPressureSection
                    assignmentId={selectedAssignment.id}
                    managerId={selectedManagerId}
                    managerName={managerName}
                    isManagerActive={!selectedAssignment.end_date}
                    tenureStartDate={selectedAssignment.start_date}
                    tenureEndDate={selectedAssignment.end_date}
                    availableSeasons={[]}
                    allAssignments={allAssignments}
                    onDataLoaded={handlePressureDataLoaded}
                  />
                </Suspense>
              )}

              <ManagerTableSection
                key={`table-${selectedManagerId}`}
                managerId={selectedManagerId}
                onSelectManager={handleSelectManager}
                onAssignmentChange={handleAssignmentChange}
              />

              {selectedAssignment && !selectedAssignment.end_date && (
                <UpcomingFixtures
                  clubId={selectedAssignment.club_id}
                  clubName={selectedAssignment.clubs.name}
                  leagueCode={selectedAssignment.league}
                />
              )}

              <Suspense fallback={null}>
                <ManagerCareerCard key={`career-${selectedManagerId}`} managerId={selectedManagerId} />
              </Suspense>

              {selectedManagerId && (
                <div className="space-y-3 pt-2">
                  <h2 className="label-micro text-muted-foreground">More on this manager</h2>

                  <Suspense fallback={null}>
                    <ManagerSeasonReports
                      managerId={selectedManagerId}
                      managerName={managerName}
                      isManagerActive={!selectedAssignment?.end_date}
                      defaultCollapsed
                    />
                  </Suspense>

                  {selectedAssignment && (
                    <Suspense fallback={null}>
                      <ManagerTalkingPoints
                        assignmentId={selectedAssignment.id}
                        managerId={selectedManagerId}
                        managerName={managerName}
                        clubName={selectedAssignment.clubs.name}
                      />
                    </Suspense>
                  )}
                </div>
              )}

            </div>
          )}

          {/* Landing page data sections */}
          {!selectedManagerId && (
            <>
              <LeagueSelectorCard
                leagues={activeLeagues}
                activeLeague={activeLeague}
                onLeagueChange={setActiveLeague}
              />

            <CurrentLeagueTables
              onSelectManager={(id) => handleSelectManager(id)}
              activeLeague={activeLeague}
            />

              <HotSeatTable
                onSelectManager={(id) => handleSelectManager(id)}
                leagueCode={activeLeague}
              />

              <Suspense fallback={null}>
                <PredictedTableCard leagueCode={activeLeague} />
              </Suspense>
            </>
          )}

        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Index;
